const DEFAULT_MINOR_UNITS = 2;
const MAX_SUPPORTED_MINOR_UNITS = 6;

// Used only when an older API response does not expose `minor_units` yet.
// The catalog returned by the server remains the source of truth.
const ISO_MINOR_UNITS_FALLBACK = Object.freeze({
  ARS: 2,
  BOB: 2,
  BRL: 2,
  CLP: 0,
  COP: 2,
  CRC: 2,
  DOP: 2,
  EUR: 2,
  GBP: 2,
  GTQ: 2,
  HNL: 2,
  JPY: 0,
  MXN: 2,
  PEN: 2,
  PYG: 0,
  USD: 2,
  UYU: 2,
});

export function normalizeCurrencyCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "";
}

export function normalizeMinorUnits(value, fallback = DEFAULT_MINOR_UNITS) {
  const numeric = Number(value);
  if (
    Number.isInteger(numeric) &&
    numeric >= 0 &&
    numeric <= MAX_SUPPORTED_MINOR_UNITS
  ) {
    return numeric;
  }
  return fallback;
}

function findCatalogCurrency(code, catalog) {
  if (Array.isArray(catalog)) {
    return catalog.find((entry) => normalizeCurrencyCode(entry?.code) === code) || null;
  }
  if (catalog && typeof catalog === "object") {
    return catalog[code] || catalog[code.toLowerCase()] || null;
  }
  return null;
}

export function getCurrencyMinorUnits(currencyCode, catalog = []) {
  const code = normalizeCurrencyCode(currencyCode);
  const entry = findCatalogCurrency(code, catalog);
  if (entry && entry.minor_units !== null && entry.minor_units !== undefined) {
    return normalizeMinorUnits(entry.minor_units);
  }
  if (Object.prototype.hasOwnProperty.call(ISO_MINOR_UNITS_FALLBACK, code)) {
    return ISO_MINOR_UNITS_FALLBACK[code];
  }
  if (code) {
    try {
      const resolved = new Intl.NumberFormat("en", {
        style: "currency",
        currency: code,
      }).resolvedOptions();
      return normalizeMinorUnits(resolved.maximumFractionDigits);
    } catch (_error) {
      // Unknown/custom legacy codes retain the historical two-decimal behavior.
    }
  }
  return DEFAULT_MINOR_UNITS;
}

export function currencyAmountStep(currencyCode, catalog = []) {
  const minorUnits = getCurrencyMinorUnits(currencyCode, catalog);
  return minorUnits === 0 ? "1" : `0.${"0".repeat(minorUnits - 1)}1`;
}

export function hasValidCurrencyPrecision(value, currencyCode, catalog = []) {
  if (value === null || value === undefined || String(value).trim() === "") return false;
  const normalized = String(value).trim().replace(",", ".");
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return false;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return false;
  const fraction = normalized.replace(/^[+-]/, "").split(".")[1] || "";
  return fraction.length <= getCurrencyMinorUnits(currencyCode, catalog);
}

export function quantizeCurrencyAmount(value, currencyCode, catalog = []) {
  const numeric = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(numeric)) return null;
  const factor = 10 ** getCurrencyMinorUnits(currencyCode, catalog);
  return Math.round((numeric + Number.EPSILON) * factor) / factor;
}

export function formatCurrencyAmount(value, currencyCode, options = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const currency = normalizeCurrencyCode(currencyCode);
  const minorUnits = options.minorUnits ?? getCurrencyMinorUnits(currency, options.catalog);
  const minimumFractionDigits = options.minimumFractionDigits ?? minorUnits;
  const maximumFractionDigits = options.maximumFractionDigits ?? minorUnits;
  try {
    return new Intl.NumberFormat(options.locale || "es-ES", {
      style: "currency",
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numeric);
  } catch (_error) {
    return `${numeric.toFixed(maximumFractionDigits)} ${currency}`.trim();
  }
}

export const currencyPrecisionDefaults = Object.freeze({
  defaultMinorUnits: DEFAULT_MINOR_UNITS,
  maxSupportedMinorUnits: MAX_SUPPORTED_MINOR_UNITS,
});
